import { FunctionComponent } from "react";
import Select, { MultiValue } from "react-select";
import { BaseInfo, Guid } from "../../../../api/baseApi";
import FormCheckbox from "../../../components/formRow/FormCheckbox";
import { FormInputNumber, FormRow, FormRowWithColumns } from "../../../components/formRow/FormRow";
import { PriorityListSettings } from "./types";

interface Props {
    settings: PriorityListSettings;
    tags: BaseInfo[];
    onUpdate: (listId: Guid, updates: Partial<PriorityListSettings>) => void;
}

const PriorityListSettingsBlock: FunctionComponent<Props> = ({ settings, tags, onUpdate }) => {
    const update = (updates: Partial<PriorityListSettings>) => onUpdate(settings.listId, updates);
    const playlistOptions = settings.nestedPlayLists.map(it => ({ value: it.id, label: it.name }));

    return (
        <div className="mb-20" style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '15px' }}>{settings.listName}</h4>
            <FormRowWithColumns>
                <FormRow>
                    <div className="d-flex align-items-center" style={{ paddingTop: "30px" }}>
                        <FormCheckbox
                            id={`use-or-${settings.listId}`}
                            checked={settings.isUseOrRelationship}
                            onChange={(v) => update({ isUseOrRelationship: v })}
                        />
                        <span className="status-block">
                            {settings.isUseOrRelationship
                                ? <span className="current-status online">&nbsp;Contain any tags</span>
                                : <span className="current-status offline">&nbsp;Contain any tags</span>}
                        </span>
                    </div>
                </FormRow>
                <FormRow>
                    <label>Included Tags</label>
                    <Select
                        classNamePrefix="select"
                        isClearable={true}
                        options={tags.filter(it => !settings.excludedTags.some(it2 => it2.id === it.id))}
                        isMulti={true}
                        filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option.id}`}
                        onChange={(v: MultiValue<BaseInfo>) => update({ includedTags: [...v] })}
                        value={settings.includedTags}
                    />
                </FormRow>
                <FormRow>
                    <label>Excluded Tags</label>
                    <Select
                        classNamePrefix="select"
                        isClearable={true}
                        options={tags.filter(it => !settings.includedTags.some(it2 => it2.id === it.id))}
                        isMulti={true}
                        filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option.id}`}
                        onChange={(v: MultiValue<BaseInfo>) => update({ excludedTags: [...v] })}
                        value={settings.excludedTags}
                    />
                </FormRow>
            </FormRowWithColumns>
            <FormRow>
                <div className="d-flex align-items-center">
                    <FormCheckbox
                        id={`use-nested-${settings.listId}`}
                        checked={settings.isUseNestedPlaylist}
                        onChange={(v) => update({ isUseNestedPlaylist: v })}
                    />
                    <span className="status-block">
                        {settings.isUseNestedPlaylist
                            ? <span className="current-status online">&nbsp;Enable Nested Playlist</span>
                            : <span className="current-status offline">&nbsp;Enable Nested Playlist</span>}
                    </span>
                </div>
            </FormRow>
            {settings.isUseNestedPlaylist && <>
                <FormRowWithColumns>
                    <FormRow>
                        <FormInputNumber
                            id={`nested-duration-${settings.listId}`}
                            title="Play nested list using below interval, in minutes"
                            isError={false}
                            errorMessage=""
                            onChangeValue={value => {
                                if (value === "" || +value < 0) update({ nestedPlaylistPeriod: '' });
                                else update({ nestedPlaylistPeriod: +value });
                            }}
                            onChange={() => { }}
                            value={settings.nestedPlaylistPeriod}
                        />
                    </FormRow>
                    <FormRow>
                        <label>Mid Roll Playlist</label>
                        <Select
                            classNamePrefix="select"
                            isClearable
                            options={playlistOptions}
                            onChange={(v) => update({ selectedNestedPlaylist: v })}
                            value={settings.selectedNestedPlaylist}
                        />
                    </FormRow>
                </FormRowWithColumns>
                <FormRowWithColumns>
                    <FormRow>
                        <label>Pre Roll Playlist</label>
                        <Select
                            classNamePrefix="select"
                            isClearable
                            options={playlistOptions}
                            onChange={(v) => update({ selectedPreRollPlaylist: v })}
                            value={settings.selectedPreRollPlaylist}
                        />
                    </FormRow>
                    <FormRow>
                        <label>Post Roll Playlist</label>
                        <Select
                            classNamePrefix="select"
                            isClearable
                            options={playlistOptions}
                            onChange={(v) => update({ selectedPostRollPlaylist: v })}
                            value={settings.selectedPostRollPlaylist}
                        />
                    </FormRow>
                </FormRowWithColumns>
            </>}
        </div>
    );
};

export default PriorityListSettingsBlock;
